import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { notifications } from '@mantine/notifications';
import type { AuthSessionUser } from '@domain/auth/types';
import {
  fetchUserProfile,
  ProfileConflictError,
  updateUserProfile,
  uploadProfileImage,
} from '@domain/profile/api';
import type { UserProfileUpdateInput } from '@domain/profile/types';
import { getErrorMessage } from '@shared/utils/ui';
import { processImage } from '@shared/media/processing/imageProcessor';
import {
  createProfileDraft,
  type ProfileDraft,
  type ProfileImageField,
} from '../../profile/components/ProfileSettingsForm';
import type { DirectoryMap } from '@domain/chat/types';

type UseProfileSettingsOptions = {
  apiBase: string;
  authUser: AuthSessionUser | null;
  contactsById: DirectoryMap;
  refreshSession: () => Promise<AuthSessionUser | null>;
  setContactsById: Dispatch<SetStateAction<DirectoryMap>>;
  pushLog: (msg: string, kind?: 'info' | 'in' | 'out' | 'error' | 'success' | 'warn') => void;
};

export function useProfileSettings({
  apiBase,
  authUser,
  contactsById,
  refreshSession,
  setContactsById,
  pushLog,
}: UseProfileSettingsOptions) {
  const authUserId = authUser?.id ?? null;
  const [profileDraft, setProfileDraft] = useState<ProfileDraft>(createProfileDraft(null));
  const [profileSnapshot, setProfileSnapshot] = useState<ProfileDraft>(createProfileDraft(null));
  const [profileEtag, setProfileEtag] = useState('');
  const [profileLoadState, setProfileLoadState] = useState<'idle' | 'loading' | 'saving' | 'error'>('idle');
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileUploadingField, setProfileUploadingField] = useState<ProfileImageField | null>(null);

  const profileDirty = useMemo(
    () => JSON.stringify(profileDraft) !== JSON.stringify(profileSnapshot),
    [profileDraft, profileSnapshot],
  );

  // A successful profile save refreshes the auth principal so its canonical
  // display name stays current. That refresh creates a new object for the same
  // account, but it must not restart profile loading: the auth payload has no
  // avatar/background fields and would temporarily replace the just-committed
  // snapshot with an incomplete fallback. Reload only when the API origin or
  // authenticated account identity changes.
  useEffect(() => {
    if (!authUser) {
      return;
    }

    let cancelled = false;
    const fallbackDraft = createProfileDraft(authUser);
    setProfileDraft(fallbackDraft);
    setProfileSnapshot(fallbackDraft);
    setProfileLoadState('loading');
    setProfileError(null);

    fetchUserProfile(apiBase)
      .then((snapshot) => {
        if (cancelled) {
          return;
        }
        const nextDraft = createProfileDraft(authUser, snapshot.profile);
        setProfileDraft(nextDraft);
        setProfileSnapshot(nextDraft);
        setProfileEtag(snapshot.etag);
        setProfileLoadState('idle');
        setContactsById((prev) => {
          const current = prev[authUser.id];
          if (!current) {
            return prev;
          }
          return {
            ...prev,
            [authUser.id]: {
              ...current,
              name: nextDraft.displayName || current.name,
              email: nextDraft.email || current.email,
              phone: nextDraft.phone,
              avatarUrl: nextDraft.avatar?.url ?? '',
              signInfo: nextDraft.signature,
            },
          };
        });
      })
      .catch((error) => {
        if (!cancelled) {
          setProfileLoadState('error');
          setProfileError(`资料接口暂不可用，当前先显示本地信息。${getErrorMessage(error)}`);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [apiBase, authUserId, setContactsById]);

  const resetProfileState = useCallback(() => {
    setProfileDraft(createProfileDraft(null));
    setProfileSnapshot(createProfileDraft(null));
    setProfileEtag('');
    setProfileLoadState('idle');
    setProfileError(null);
    setProfileUploadingField(null);
  }, []);

  const handleProfileDraftChange = useCallback((field: keyof ProfileDraft, value: string) => {
    setProfileDraft((prev) => ({ ...prev, [field]: value }));
    setProfileError(null);
  }, []);

  const handleProfileImagePick = useCallback(async (field: ProfileImageField, file: File) => {
    setProfileUploadingField(field);
    setProfileError(null);
    try {
      const use = field === 'avatar' ? 'avatar' : 'profileBackground';
      const processed = await processImage(file, { use, quality: 'standard' });
      const media = await uploadProfileImage(processed.file, apiBase, {
        ...processed.metadata,
        policyVersion: processed.policyVersion,
        quality: 'standard',
        sourceSizeBytes: processed.source.sizeBytes,
        outputSizeBytes: processed.metadata.sizeBytes,
      });
      const locator = {
        provider: 's3',
        url: media.url,
      };
      setProfileDraft((prev) => ({
        ...prev,
        [field]: locator,
        [field === 'avatar' ? 'avatarAssetId' : 'homepageBackgroundAssetId']: media.id,
      }));
      notifications.show({
        title: field === 'avatar' ? '头像已上传' : '主页背景已上传',
        message: '保存资料后生效',
        color: 'green',
        autoClose: 3000,
      });
    } catch (error) {
      setProfileError(`上传失败：${getErrorMessage(error)}`);
    } finally {
      setProfileUploadingField(null);
    }
  }, [apiBase]);

  const handleResetProfileDraft = useCallback(() => {
    setProfileDraft(profileSnapshot);
    setProfileError(null);
    setProfileLoadState('idle');
  }, [profileSnapshot]);

  const handleSaveProfile = useCallback(async () => {
    if (!authUser) {
      return;
    }

    // Send only fields changed from the loaded base snapshot. This preserves
    // PATCH omission semantics and prevents an editor from overwriting a field
    // it never touched on another device.
    const payload: UserProfileUpdateInput = {};
    if (profileDraft.displayName !== profileSnapshot.displayName) {
      payload.displayName = profileDraft.displayName.trim();
    }
    if (profileDraft.signature !== profileSnapshot.signature) {
      payload.signature = profileDraft.signature.trim() || null;
    }
    if (profileDraft.avatarAssetId !== profileSnapshot.avatarAssetId) {
      payload.avatarAssetId = profileDraft.avatarAssetId || null;
    }
    if (profileDraft.homepageBackgroundAssetId !== profileSnapshot.homepageBackgroundAssetId) {
      payload.homepageBackgroundAssetId = profileDraft.homepageBackgroundAssetId || null;
    }

    setProfileLoadState('saving');
    setProfileError(null);

    try {
      const updated = await updateUserProfile(payload, profileEtag, apiBase);
      const nextDraft = createProfileDraft(authUser, updated.profile);

      setProfileDraft(nextDraft);
      setProfileSnapshot(nextDraft);
      setProfileEtag(updated.etag);
      setContactsById((prev) => {
        const current = prev[authUser.id];
        if (!current) {
          return prev;
        }
        return {
          ...prev,
          [authUser.id]: {
            ...current,
            name: nextDraft.displayName || current.name,
            email: nextDraft.email || current.email,
            phone: nextDraft.phone,
            avatarUrl: nextDraft.avatar?.url ?? '',
            signInfo: nextDraft.signature,
          },
        };
      });
      await refreshSession();
      setProfileLoadState('idle');
      notifications.show({
        title: '资料已保存',
        message: '个人资料已更新',
        color: 'green',
        autoClose: 3000,
      });
      pushLog('个人资料已更新', 'success');
    } catch (error) {
      if (error instanceof ProfileConflictError) {
        const current = createProfileDraft(authUser, error.current.profile);
        const conflictFields: string[] = [];
        const merged = { ...profileDraft };

        // Three-way merge each canonical editable field against the base that
        // was originally loaded. Disjoint changes merge automatically; a true
        // same-field conflict keeps the local draft visible and asks the user
        // to review before saving again with the new ETag.
        const mergeText = (
          key: 'displayName' | 'signature',
          label: string,
        ) => {
          const localChanged = profileDraft[key] !== profileSnapshot[key];
          const serverChanged = current[key] !== profileSnapshot[key];
          if (!localChanged) {
            merged[key] = current[key];
          } else if (serverChanged && profileDraft[key] !== current[key]) {
            conflictFields.push(label);
          }
        };
        mergeText('displayName', '昵称');
        mergeText('signature', '个性签名');

        const mergeMedia = (
          idKey: 'avatarAssetId' | 'homepageBackgroundAssetId',
          mediaKey: 'avatar' | 'homepageBackground',
          label: string,
        ) => {
          const localChanged = profileDraft[idKey] !== profileSnapshot[idKey];
          const serverChanged = current[idKey] !== profileSnapshot[idKey];
          if (!localChanged) {
            merged[idKey] = current[idKey];
            merged[mediaKey] = current[mediaKey];
          } else if (serverChanged && profileDraft[idKey] !== current[idKey]) {
            conflictFields.push(label);
          }
        };
        mergeMedia('avatarAssetId', 'avatar', '头像');
        mergeMedia(
          'homepageBackgroundAssetId',
          'homepageBackground',
          '主页背景',
        );

        setProfileDraft(merged);
        setProfileSnapshot(current);
        setProfileEtag(error.current.etag);
        setProfileLoadState('error');
        setProfileError(
          conflictFields.length > 0
            ? `其他设备修改了${conflictFields.join('、')}。已保留你的版本，请确认后再次保存`
            : '已合并其他设备的最新资料，请再次保存你的更改',
        );
        return;
      }
      setProfileLoadState('error');
      setProfileError(`保存失败：${getErrorMessage(error)}`);
      pushLog(`更新个人资料失败: ${getErrorMessage(error)}`, 'error');
    }
  }, [
    apiBase,
    authUser,
    contactsById,
    profileDraft,
    profileEtag,
    profileSnapshot,
    pushLog,
    refreshSession,
    setContactsById,
  ]);

  return {
    profileDraft,
    profileSnapshot,
    profileLoadState,
    profileError,
    profileDirty,
    profileUploadingField,
    resetProfileState,
    handleProfileDraftChange,
    handleProfileImagePick,
    handleResetProfileDraft,
    handleSaveProfile,
  };
}
